import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Box from '@material-ui/core/Box';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import ItemAdjustment from '../../../ItemsList/ItemAdjustment';
import TextField from '@afosto/app-components-react/TextField';
import formatAdjustments from '../../../../utils/formatAdjustments';
import formatCurrency from '@afosto/utils/formatCurrency';
import styled from 'styled-components';
import translations from './translations';

const OrderItemQuantity = styled.div`
  width: 70px;
  flex-shrink: 0;
`;

const OrderItemTotal = styled.div`
  padding-bottom: ${props => props.theme.spacing()}px;
  padding-left: ${props => props.theme.spacing(2)}px;
  min-width: 100px;
  text-align: right;
`;

const OrderItem = ({
  allowNegativeQuantity,
  disableQuantity,
  item,
  onQuantity,
  SelectionComponent,
  isVatShifted,
  isIncludingVat,
}) => {
  const intl = useIntl();
  const hasDescription = item.description && item.description !== '';
  let itemQuantity = item?.quantity;

  if (itemQuantity && !allowNegativeQuantity) {
    itemQuantity = Math.abs(itemQuantity);
  }

  const [quantity, setQuantity] = useState(itemQuantity ?? 1);
  const adjustments = formatAdjustments(item?.adjustments ?? []);

  useEffect(() => {
    setQuantity(itemQuantity);
  }, [itemQuantity]);

  const handleQuantity = () => {
    if (onQuantity && typeof onQuantity === 'function') {
      onQuantity(item)(quantity);
    }
  };

  return (
    <Box display="flex">
      {SelectionComponent && <SelectionComponent />}
      <Box display="flex" flex={1} px={2} alignItems="flex-end">
        <Box flex={1}>
          <ListItemText
            primary={hasDescription ? item.description : item.sku}
            secondaryTypographyProps={{
              component: 'div',
            }}
            secondary={
              <Box pr={3}>
                {hasDescription && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">{item.sku}</Typography>
                  </Box>
                )}
                {(item.quantity > 1 || item.quantity < 0) && (
                  <Typography variant="body2">
                    {item.quantity}x{' '}
                    {formatCurrency(item.amount ? item.amount / 100 : 0, item.currency)} (
                    {isIncludingVat
                      ? intl.formatMessage(translations.totals.incl)
                      : intl.formatMessage(translations.totals.excl)}
                    )
                  </Typography>
                )}
                <Typography variant="body2">
                  {intl.formatMessage(translations.totals.vat)}{' '}
                  {isVatShifted && intl.formatMessage(translations.totals.vatShifted)}{' '}
                  {item?.vat_percentage}%
                </Typography>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">
                    {intl.formatMessage(translations.totals.subtotal)} (
                    {isIncludingVat
                      ? intl.formatMessage(translations.totals.incl)
                      : intl.formatMessage(translations.totals.excl)}
                    ){' '}
                  </Typography>
                  {formatCurrency(item.subtotal / 100)}
                </Box>
                {adjustments.map(adjustment => (
                  <ItemAdjustment key={adjustment?.reference} adjustment={adjustment} />
                ))}
                <Box my={2}>
                  <Typography variant="subtitle2" color="textPrimary">
                    {intl.formatMessage(translations.totals.total)} (
                    {isIncludingVat
                      ? intl.formatMessage(translations.totals.incl)
                      : intl.formatMessage(translations.totals.excl)}
                    )
                  </Typography>
                </Box>
              </Box>
            }
          />
        </Box>
        <OrderItemQuantity>
          <TextField
            name={`item[${item?.id}]`}
            variant="outlined"
            margin="dense"
            fullWidth
            value={quantity}
            onChange={(ev, newValue) => setQuantity(newValue)}
            onClick={event => event?.target?.select()}
            onBlur={handleQuantity}
            disabled={disableQuantity}
          />
        </OrderItemQuantity>
        <OrderItemTotal>
          <Typography style={{ alignSelf: 'flex-end', marginBottom: 5 }} variant="subtitle2">
            {formatCurrency(item.total ? item.total / 100 : 0, item.currency)}
          </Typography>
        </OrderItemTotal>
      </Box>
    </Box>
  );
};

OrderItem.propTypes = {
  allowNegativeQuantity: PropTypes.bool,
  disableQuantity: PropTypes.bool,
  item: PropTypes.object,
  isIncludingVat: PropTypes.bool,
  isVatShifted: PropTypes.bool,
  onQuantity: PropTypes.func,
  SelectionComponent: PropTypes.func,
};

OrderItem.defaultProps = {
  allowNegativeQuantity: true,
  disableQuantity: false,
  item: {},
  isIncludingVat: true,
  isVatShifted: false,
  onQuantity: undefined,
  SelectionComponent: undefined,
};

export default OrderItem;
